import { useState } from "react";

import ActionButton from "@/components/Button/ActionButton";
import ToggleButton from "@/components/Button/ToggleButton";
import { HStack, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Separator } from "@/components/ui/separator";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { addNetworkDynamicSchema } from "../-schema/add-network-schema";
import { useAddNetworkFormStore } from "../-store/add-network-form-store";

const calculatePercentage = (filled: number, total: number) =>
  (filled / Math.max(total, 1)) * 100;

interface ProgressStats {
  total: number;
  filled: number;
  required: number;
  filledRequired: number;
}

export function ProgressCard() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.add.progressbar",
  });
  const store = useAddNetworkFormStore();
  const { mustOnly, toggleMustOnly } = store;
  const [isExpanded, setIsExpanded] = useState(true);

  // スキーマユーティリティを用いた集計
  const countFields = (): ProgressStats => {
    const values = {
      networkName: store.networkName,
      networkType: store.networkType,
      connectionType: store.connectionType,
      networkCategory: store.networkCategory,
      purpose: store.purpose,
      destinationType: store.destinationType,
      placeId: store.placeId,
      deviceId: store.deviceId,
      adminContact: store.adminContact,
      accessLevel: store.accessLevel,
      accessStatus: store.accessStatus,
      accessPeriodStartDate: store.accessPeriodStartDate,
      accessPeriodEndDate: store.accessPeriodEndDate,
      subjectType: store.subjectType,
    } as const;

    const totalCount = addNetworkDynamicSchema.getTotalFields(values);
    const filledCount = addNetworkDynamicSchema.getInputCount(values);
    const requiredCount = addNetworkDynamicSchema.getRequiredTotalFields();
    const filledRequiredCount =
      addNetworkDynamicSchema.getRequiredInputCount(values);

    return {
      total: totalCount,
      filled: filledCount,
      required: requiredCount,
      filledRequired: filledRequiredCount,
    };
  };

  const { total, filled, required, filledRequired } = countFields();

  const toggleCard = () => setIsExpanded(!isExpanded);

  const CircularProgress = ({
    filled,
    total,
    color,
  }: {
    filled: number;
    total: number;
    color: string;
  }) => {
    const percentage = calculatePercentage(filled, total);
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <HStack className="relative items-center">
        <svg className="w-24 h-24 transform -rotate-90">
          <title>Progress Circle</title>
          <circle
            className="text-gray-200"
            strokeWidth="6"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx="50%"
            cy="50%"
          />
          <circle
            style={{ stroke: color }}
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="transparent"
            r={radius}
            cx="50%"
            cy="50%"
          />
        </svg>
        <HStack className="items-end ml-4">
          <Text variant="numberSemiLarge"> {filled} </Text>
          <Spacer variant="xxs" />/<Spacer variant="xxs" />
          <Text variant="numberSemiLarge"> {total} </Text>
          <Spacer variant="xs" />
          <Text variant="labelSemiLarge" className="text-neutral-on-fill">
            {t("filled")}
          </Text>
        </HStack>
      </HStack>
    );
  };

  return (
    <VStack
      className={`relative h-full rounded-lg bg-white shadow-md transition-all duration-300 ${isExpanded ? "p-6 w-80" : "w-10 h-10"}`}
    >
      {/* Card Content - Only shows when expanded */}
      {isExpanded ? (
        <VStack>
          <ActionButton
            icon={isExpanded ? "PanelLeftCloseIcon" : "PanelLeftOpenIcon"}
            iconColor="primary"
            variant="ghost"
            onClick={toggleCard}
            className="absolute top-2 left-2"
          />
          <Spacer variant="l" />
          <Separator />
          {/* Total Section */}
          <VStack className="gap-4">
            <Separator />
            <Text variant="labelSemiLarge" className="font-semibold">
              {t("total")}
            </Text>
            <span className="flex justify-center items-center">
              <CircularProgress filled={filled} total={total} color="#1E3A8A" />
            </span>
            <Separator />
          </VStack>

          {/* Must Item Section */}
          <VStack className="gap-4">
            <Separator />
            <Text variant="labelSemiLarge" className="font-semibold">
              {t("mustItem")}
            </Text>
            <span className="flex justify-center items-center">
              <CircularProgress
                filled={filledRequired}
                total={required}
                color="#6B4E3D"
              />
            </span>
            <Separator />
          </VStack>

          {/* Mode Settings */}
          <VStack className="gap-4 items-start">
            <Separator />
            <Text variant="labelSemiLarge" className="font-semibold">
              {t("modeSetting")}
            </Text>
            <ToggleButton
              checked={mustOnly}
              onCheckedChange={toggleMustOnly}
              label={t("mustItemOnly") as string}
              labelPosition="left"
            />
          </VStack>
        </VStack>
      ) : (
        <ActionButton
          icon={isExpanded ? "PanelLeftCloseIcon" : "PanelLeftOpenIcon"}
          iconColor="primary"
          variant="ghost"
          onClick={toggleCard}
          className="flex justify-center items-center mx-auto"
        />
      )}
    </VStack>
  );
}
